/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';
import * as colors from '@atlaskit/theme/colors';
import { useBackendFeatureFlag } from '../../common/providers/app-switcher-bff-backend-feature-flags-provider';

import { GuardIcon } from '@atlaskit/temp-nav-app-icons/guard';
import { BitbucketIcon } from '@atlaskit/temp-nav-app-icons/bitbucket';
import { CompassIcon } from '@atlaskit/temp-nav-app-icons/compass';
import { ConfluenceIcon } from '@atlaskit/temp-nav-app-icons/confluence';
import { JiraProductDiscoveryIcon } from '@atlaskit/temp-nav-app-icons/jira-product-discovery';
import { JiraServiceManagementIcon } from '@atlaskit/temp-nav-app-icons/jira-service-management';
import { JiraIcon } from '@atlaskit/temp-nav-app-icons/jira';
import { LoomIcon } from '@atlaskit/temp-nav-app-icons/loom';
import { FocusIcon } from '@atlaskit/temp-nav-app-icons/focus';
import { AnalyticsIcon } from '@atlaskit/temp-nav-app-icons/analytics';
import { OpsgenieIcon } from '@atlaskit/temp-nav-app-icons/opsgenie';
import { RovoIcon } from '@atlaskit/temp-nav-app-icons/rovo';
import { StatuspageIcon } from '@atlaskit/temp-nav-app-icons/statuspage';
import { AssetsIcon } from '@atlaskit/temp-nav-app-icons/assets';
import { TrelloIcon } from '@atlaskit/temp-nav-app-icons/trello';
import { AdminIcon } from '@atlaskit/temp-nav-app-icons/admin';
import { CompanyHubIcon } from '@atlaskit/temp-nav-app-icons/company-hub';
import { CustomLinkIcon } from '@atlaskit/temp-nav-app-icons/custom-link';
import { SearchIcon } from '@atlaskit/temp-nav-app-icons/search';
import { ChatIcon } from '@atlaskit/temp-nav-app-icons/chat';
import { StudioIcon } from '@atlaskit/temp-nav-app-icons/studio';
import { TeamsIcon } from '@atlaskit/temp-nav-app-icons/teams';
import { GoalsIcon } from '@atlaskit/temp-nav-app-icons/goals';
import { HomeIcon } from '@atlaskit/temp-nav-app-icons/home';
import { TalentIcon } from '@atlaskit/temp-nav-app-icons/talent';

import {
	AtlassianIcon,
	JiraSoftwareIcon,
	JiraWorkManagementIcon,
	AtlassianAnalyticsIcon,
	type LogoProps,
} from '@atlaskit/logo';

const styles = cssMap({
	root: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: token('space.400'),
		height: token('space.400'),
		borderRadius: token('border.radius.200'),
	},
});

// The product keys need to match the iconKey registered by products in Flight Deck:
// iconKey: https://bitbucket.org/atlassian/flight-deck/src/master/assets/image/logos/contrib/
// registered products: https://bitbucket.org/atlassian/flight-deck/src/master/packages/integrations/twp-switch-to-products/contrib/
type FlightDeckIconKey =
	| 'atlassian-analytics'
	| 'guard'
	| 'bitbucket'
	| 'compass'
	| 'confluence'
	| 'atlassian'
	| 'atlassian-home'
	| 'jira-product-discovery'
	| 'jira-service-management'
	| 'jira-software'
	| 'jira-work-management'
	| 'jira'
	| 'loom'
	| 'mercury'
	| 'opsgenie'
	| 'rovo'
	| 'statuspage'
	| 'talent'
	| 'trello'
	| 'townsquare';

// those icon keys are only used internally in the app
export type InternalIconKey =
	| 'atlassian-analytics-rebrand'
	| 'company-hub'
	| 'assets'
	| 'admin'
	| 'custom-link'
	| 'search'
	| 'chat'
	| 'studio'
	| 'teams'
	| 'goals';

export type AppIconKey = FlightDeckIconKey | InternalIconKey;

interface IconBaseProps {
	backgroundColor?: string;
	children: React.ReactNode;
	testId?: string;
}

const IconBase = ({ backgroundColor, children, testId }: IconBaseProps) => (
	<div
		aria-hidden={true}
		data-testid={testId}
		css={styles.root}
		style={{
			backgroundColor,
		}}
	>
		{children}
	</div>
);

interface TempNavAppIconProps {
	size?: '20' | '24' | '32';
	label: string;
	appearance: 'brand' | 'legacy';
}

interface Options {
	testId?: string;
	label?: string;
	appearance?: 'brand' | 'legacy';
}

// HOC for '@atlaskit/temp-nav-app-icons'
export const createTempNavAppIcon = (
	InnerIcon: React.ComponentType<TempNavAppIconProps>,
	options?: Options,
) => {
	return (
		<IconBase testId={options?.testId}>
			<InnerIcon
				label={options?.label || ''}
				size="32"
				appearance={options?.appearance || 'legacy'}
			/>
		</IconBase>
	);
};

// HOC for '@atlaskit/logo'
// Hard code icon color and background color for product icons
// As we want to use the same  color for both dark and light themes
export const createAtlasKitAppIcon = (
	InnerIcon: React.ComponentType<LogoProps>,
	options?: Options,
) => {
	return (
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		<IconBase backgroundColor="#1868DB" testId={options?.testId}>
			{/* eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage */}
			<InnerIcon iconColor={colors.N0} size="small" />
		</IconBase>
	);
};

interface AppIconProps {
	iconKey: AppIconKey;
	testId?: string;
}

export const AppIcon = ({ iconKey, testId }: AppIconProps) => {
	const featureFlags = useBackendFeatureFlag();

	const appearance =
		featureFlags.isTeam25AppRebrandEnabled || fg('platform-team25-app-icon-tiles')
			? 'brand'
			: 'legacy';

	switch (iconKey) {
		case 'assets':
			return createTempNavAppIcon(AssetsIcon, { testId, appearance });
		case 'atlassian-analytics':
			return createAtlasKitAppIcon(AtlassianAnalyticsIcon, { testId, appearance });
		case 'atlassian-analytics-rebrand':
			return createTempNavAppIcon(AnalyticsIcon, { testId, appearance });
		case 'guard':
			return createTempNavAppIcon(GuardIcon, { testId, appearance });
		case 'bitbucket':
			return createTempNavAppIcon(BitbucketIcon, { testId, appearance });
		case 'compass':
			return createTempNavAppIcon(CompassIcon, { testId, appearance });
		case 'confluence':
			return createTempNavAppIcon(ConfluenceIcon, { testId, appearance });
		case 'company-hub':
			return createTempNavAppIcon(CompanyHubIcon, { testId, appearance });
		case 'atlassian':
			return createAtlasKitAppIcon(AtlassianIcon, { testId, appearance });
		case 'atlassian-home':
			return createTempNavAppIcon(HomeIcon, { testId, appearance });
		case 'jira-product-discovery':
			return createTempNavAppIcon(JiraProductDiscoveryIcon, { testId, appearance });
		case 'jira-service-management':
			return createTempNavAppIcon(JiraServiceManagementIcon, { testId, appearance });
		case 'jira-software':
			return createAtlasKitAppIcon(JiraSoftwareIcon, { testId, appearance });
		case 'jira-work-management':
			return createAtlasKitAppIcon(JiraWorkManagementIcon, { testId, appearance });
		case 'jira':
			return createTempNavAppIcon(JiraIcon, { testId, appearance });
		case 'loom':
			return createTempNavAppIcon(LoomIcon, { testId, appearance });
		case 'mercury':
			return createTempNavAppIcon(FocusIcon, { testId, appearance });
		case 'opsgenie':
			return createTempNavAppIcon(OpsgenieIcon, { testId, appearance });
		case 'rovo':
			return createTempNavAppIcon(RovoIcon, { testId, appearance });
		case 'statuspage':
			return createTempNavAppIcon(StatuspageIcon, { testId, appearance });
		case 'talent':
			return createTempNavAppIcon(TalentIcon, { testId, appearance });
		case 'trello':
			return createTempNavAppIcon(TrelloIcon, { testId, appearance });
		case 'townsquare':
			return createAtlasKitAppIcon(AtlassianIcon, { testId, appearance });
		case 'admin':
			return createTempNavAppIcon(AdminIcon, { testId, appearance });
		case 'custom-link':
			return createTempNavAppIcon(CustomLinkIcon, {
				testId,
				label: 'Custom link',
				appearance,
			});
		case 'chat':
			return createTempNavAppIcon(ChatIcon, { testId, appearance });
		case 'search':
			return createTempNavAppIcon(SearchIcon, { testId, appearance });
		case 'teams':
			return createTempNavAppIcon(TeamsIcon, { testId, appearance });
		case 'goals':
			return createTempNavAppIcon(GoalsIcon, { testId, appearance });
		case 'studio':
			return createTempNavAppIcon(StudioIcon, { testId, appearance });
		// fallback to atlassian icon
		default:
			return createAtlasKitAppIcon(AtlassianIcon, { testId });
	}
};
